import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { MstatViberConfig, MstatViberIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { integrationRulesMstatViber as rules } from '@/util/validation/rules/integration-rules';

interface Props {
  integration: MstatViberIntegration;
}

type AuthMethod = 'api_token' | 'basic';

type CredentialsForm = {
  account_id: string;
  auth_method: AuthMethod;
  api_token: string;
  username: string;
  password: string;
};

const FORM_ID = 'mstat-viber-credentials-form';

const toDefaults = (integration: MstatViberIntegration): CredentialsForm => ({
  account_id: integration.config?.account_id ? String(integration.config.account_id) : '',
  // `username` isn't redacted on read, so its presence tells us basic auth was used.
  auth_method: integration.config?.username ? 'basic' : 'api_token',
  api_token: integration.config?.api_token ?? '',
  username: integration.config?.username ?? '',
  password: integration.config?.password ?? '',
});

/**
 * MSTAT API credentials card. Authentication is an explicit either/or: the radio
 * selects API token *or* username + password, and only the chosen method's
 * fields render (and validate) — mirroring the backend's `api_token` XOR
 * (`username` + `password`) rule. Editable: writers receive the real secrets on
 * read (the backend only masks for non-writers), so they can update them here.
 */
const CredentialsCard = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<MstatViberIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const form = useForm<CredentialsForm>({
    mode: 'onSubmit',
    defaultValues: toDefaults(integration),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const authMethod = useWatch({ control, name: 'auth_method' });

  const onSubmit = (values: CredentialsForm) => {
    const config: MstatViberConfig = {
      ...integration.config,
      account_id: Number(values.account_id),
    };

    // Persist exactly one auth method; drop the other so switching never leaves
    // stale credentials behind.
    if (values.auth_method === 'api_token') {
      config.api_token = values.api_token;
      delete config.username;
      delete config.password;
    } else {
      config.username = values.username;
      config.password = values.password;
      delete config.api_token;
    }

    updateIntegration({ ...integration, config }, { onSuccess: () => reset(values) });
  };

  return (
    <div className="w-full">
      <p className="mb-2 px-1.5 text-sm leading-none font-medium text-foreground-muted">
        {t('integrations.mstat_viber_connect.title')}
      </p>
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              <FormField
                control={control}
                name="account_id"
                rules={{
                  required: t('common.required_field'),
                  pattern: {
                    value: /^[1-9][0-9]*$/,
                    message: t('integrations.mstat_viber_connect.account_id.invalid'),
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.mstat_viber_connect.account_id.title')}</FormLabel>
                    <FormControl>
                      <Input
                        id="account_id"
                        inputMode="numeric"
                        placeholder={t('integrations.mstat_viber_connect.account_id.placeholder')}
                        disabled={!canWrite}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('integrations.mstat_viber_connect.account_id.subtitle')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="auth_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.mstat_viber_connect.auth.title')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!canWrite}
                        className="gap-3"
                      >
                        <RadioGroupItem id="auth-api-token" value="api_token">
                          {t('integrations.mstat_viber_connect.auth.api_token_option')}
                        </RadioGroupItem>
                        <RadioGroupItem id="auth-basic" value="basic">
                          {t('integrations.mstat_viber_connect.auth.basic_option')}
                        </RadioGroupItem>
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>
                      {t('integrations.mstat_viber_connect.auth.subtitle')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {authMethod === 'api_token' ? (
                <FormField
                  control={control}
                  name="api_token"
                  rules={{
                    required: t('common.required_field'),
                    maxLength: {
                      value: rules.apiToken.maxLength,
                      message: t('validation.max_length', { 0: rules.apiToken.maxLength }),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('integrations.mstat_viber_connect.api_token.title')}</FormLabel>
                      <FormControl>
                        <Input
                          id="api_token"
                          type="password"
                          placeholder={t('integrations.mstat_viber_connect.api_token.placeholder')}
                          disabled={!canWrite}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <>
                  <FormField
                    control={control}
                    name="username"
                    rules={{
                      required: t('common.required_field'),
                      maxLength: {
                        value: rules.username.maxLength,
                        message: t('validation.max_length', { 0: rules.username.maxLength }),
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('integrations.mstat_viber_connect.username.title')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="username"
                            placeholder={t('integrations.mstat_viber_connect.username.placeholder')}
                            disabled={!canWrite}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="password"
                    rules={{
                      required: t('common.required_field'),
                      maxLength: {
                        value: rules.password.maxLength,
                        message: t('validation.max_length', { 0: rules.password.maxLength }),
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('integrations.mstat_viber_connect.password.title')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="password"
                            type="password"
                            placeholder={t('integrations.mstat_viber_connect.password.placeholder')}
                            disabled={!canWrite}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};

export default CredentialsCard;
